import React from 'react'
import clsx from 'clsx'
import { FormattedMessage } from 'react-intl'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Grid, IconButton, Button } from '@material-ui/core'
import messages from '../../../messages'
import { NGSOTypeOptions, Modifications } from '../enum'
import EditConcentration from './EditConcentration'
import Close from '@material-ui/icons/Close'

const useStyles = makeStyles((theme: Theme) => {
  return {
    gridHead: {
      backgroundColor: theme.palette.background.grey,
    },
    gridRow: {
      fontSize: theme.typography.pxToRem(12),
      borderTop: `1px solid ${theme.palette.grey[300]}`,
    },
    gridItem: {
      fontSize: theme.typography.pxToRem(14),
      padding: theme.typography.pxToRem(16),
      width: '25%',
      '&:nth-child(3)': {
        backgroundColor: theme.palette.background.grey,
      },
    },
    gridItemForSelect: {
      padding: theme.typography.pxToRem(16),
      width: '25%',
    },
    list: {
      margin: 0,
      paddingLeft: theme.typography.pxToRem(16),
    },
    label: {
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightBold,
    },
    description: {
      fontSize: theme.typography.pxToRem(12),
    },
    paddingBottom: {
      paddingBottom: theme.spacing(3.25),
    },
    ngsoSilverSection: {
      position: 'relative',
    },
    popOverArrow: {
      position: 'absolute',
      '&::before': {
        backgroundColor: 'white',
        content: '""',
        display: 'block',
        position: 'absolute',
        zIndex: 9,
        width: 12,
        height: 12,
        top: 3,
        transform: 'rotate(45deg)',
        left: 65,
        borderTop: '1px solid #707070',
        borderLeft: '1px solid #707070',
      },
    },
    popOverContainer: {
      width: 400,
      border: '1px solid #707070',
      borderRadius: 5,
      padding: theme.spacing(3, 4, 4, 4),
      position: 'absolute',
      background: '#fff',
      left: -125,
      top: 45,
      boxShadow: '0px 4px 6px 0px rgb(0 0 0 / 16%)',
    },
    closeButton: {
      float: 'right',
      padding: 5,
    },
    closeIcon: {
      color: 'black',
      fontSize: '1rem',
    },
    popOverTitle: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightMedium,
      marginBottom: theme.spacing(1.5),
    },
    popOverContent: {
      fontSize: theme.typography.pxToRem(14),
    },
  }
})

const ProductConfigurationsTable = (props) => {
  const classes = useStyles()
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [selectedConcentration, setSelectedConcentration] = React.useState()

  const [open, setPopoverOpen] = React.useState(true)
  const getNgsoType = (type: string) => {
    const selectedType = props.product?.listNgso?.find((item) => item === type)
    return !!selectedType
  }

  const UpdateNewConcentration = (newConcentration) => {
    props.onSelectNgsoType(NGSOTypeOptions.NGSOGold, newConcentration)
  }

  const handleConcentartionPopup = () => {
    if (
      props.productDetails.isCSR &&
      props.sessionData[0].wellList[0].format !== Modifications.Dry
    ) {
      setDialogOpen(true)
      setSelectedConcentration(props.sessionData[0].wellList[0].concentration)
    } else {
      props.onSelectNgsoType(
        NGSOTypeOptions.NGSOGold,
        props.sessionData[0].wellList[0].concentration
      )
    }
  }

  return (
    <div>
      <Grid
        container
        direction="row"
        className={clsx(classes.gridRow, classes.gridHead)}
      >
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          Options
        </Grid>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          NGSO-Bronze
        </Grid>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          NGSO-Silver
        </Grid>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          NGSO-Gold
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.gridRow}>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          Cross Contamination<sup>1</sup>
        </Grid>
        <Grid item className={classes.gridItem}>
          {'< 2.0% '}
        </Grid>
        <Grid item className={classes.gridItem}>
          {'< 1.0% '}
        </Grid>
        <Grid item className={classes.gridItem}>
          {'< 0.25% '}
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.gridRow}>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          Scale & Minimum Yield<sup>2</sup>
        </Grid>
        <Grid item className={classes.gridItem}>
          <ul className={classes.list}>
            <li>0.05 µmole (1-3 OD)</li>
            <li>0.2 µmole (3-8 OD)</li>
            <li>1.0 µmole (9-13 OD)</li>
          </ul>
        </Grid>
        <Grid item className={classes.gridItem}>
          * 0.2 µmole (10 nmol or 2-6 OD) length dependent
        </Grid>
        <Grid item className={classes.gridItem}>
          <ul className={classes.list}>
            <li>0.2 µmole (3-8 OD)</li>
            <li>1.0 µmole (9-13 OD)</li>
            <li>Or, final yields: 25, 50, 100, 250 & 500 nmol</li>
          </ul>
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.gridRow}>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          Purity
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          {'70-85%'}
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          {'65-80%'}{' '}
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          {'85-95%'}{' '}
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.gridRow}>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          Sequence Length
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          {'10-80 bases'}
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          {'10-60 bases'}{' '}
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          {'10-80 bases'}{' '}
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.gridRow}>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          Format
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          {'Supplied dry in tubes'}
        </Grid>
        <Grid item className={classes.gridItem}>
          <ul className={classes.list}>
            <li>Supplied in solution in tubes or 96 well plates</li>
            <li>
              Standard: 100 µM in water, TE (10 mM Tris, pH 7.5-8.0, 1 mM EDTA),
              or Tris (10 mM Tris-HCL, pH 8.0)
            </li>
            <li>Inquire: 30-150 µM in water or 30-130 µM in TE/Tris buffer</li>
            <li>Mixing Available: Yes</li>
          </ul>
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          Supplied dry or in water/buffer in tubes (pooling & aliquoting
          available){' '}
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.gridRow}>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          Platform Compatibility
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          Illumina, Thermo Fisher (Ion Torrent<sup>TM</sup>) & PacBio
          <sup>TM</sup>{' '}
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          Thermo Fisher (Ion Torrent<sup>TM</sup>){' '}
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          Illumina, Thermo Fisher (Ion Torrent<sup>TM</sup>) & PacBio
          <sup>TM</sup>{' '}
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.gridRow}>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          Quality Control
        </Grid>
        <Grid item className={classes.gridItem}>
          <ul className={classes.list}>
            <li>100% Mass Spectrometry</li>
            <li>Analytical HPLC available</li>
            <li>Certificate of Analysis available</li>
          </ul>
        </Grid>
        <Grid item className={classes.gridItem}>
          <ul className={classes.list}>
            <li>100% Mass Spectrometry</li>
            <li>Analytical HPLC available</li>
            <li>Certificate of Analysis available</li>
          </ul>
        </Grid>
        <Grid item className={classes.gridItem}>
          <ul className={classes.list}>
            <li>100% Mass Spectrometry</li>
            <li>Analytical HPLC available</li>
            <li>Certificate of Analysis available</li>
          </ul>
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.gridRow}>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          Quality Management System
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          ISO 9001{' '}
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          ISO 9001{' '}
        </Grid>
        <Grid item className={classes.gridItem}>
          {' '}
          ISO 13485{' '}
        </Grid>
      </Grid>

      <Grid container direction="row" className={classes.gridRow}>
        <Grid
          item
          className={clsx(classes.gridItemForSelect, classes.label)}
        ></Grid>
        <Grid item className={classes.gridItemForSelect}>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={
              props.sessionData[0].isSpecificationsOverridden
                ? false
                : !getNgsoType(NGSOTypeOptions.NGSOBronze)
            }
            onClick={() =>
              props.onSelectNgsoType(
                NGSOTypeOptions.NGSOBronze,
                props.sessionData[0].wellList[0].concentration
              )
            }
          >
            <FormattedMessage {...messages.SELECT} />
          </Button>
        </Grid>
        <Grid item className={classes.gridItemForSelect}>
          <div className={classes.ngsoSilverSection}>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={
                props.sessionData[0].isSpecificationsOverridden
                  ? false
                  : !getNgsoType(NGSOTypeOptions.NGSOSilver)
              }
              onClick={() =>
                props.onSelectNgsoType(
                  NGSOTypeOptions.NGSOSilver,
                  props.sessionData[0].wellList[0].concentration
                )
              }
            >
              <FormattedMessage {...messages.SELECT} />
            </Button>
            {props.sessionData[0].wellList[0].format !== Modifications.Dry &&
            props.sessionData[0].wellList[0].scale !== Modifications.Scale &&
            props.sessionData[0].wellList.length < 12
              ? open && (
                  <div>
                    <div className={classes.popOverArrow}> </div>
                    <div className={classes.popOverContainer}>
                      <IconButton
                        onClick={() => setPopoverOpen(false)}
                        className={classes.closeButton}
                      >
                        <Close className={classes.closeIcon} />
                      </IconButton>
                      <div className={classes.popOverTitle}>NGSO Silver</div>
                      <div className={classes.popOverContent}>
                        A minimum of 12 items required to order NGSO Silver
                      </div>
                    </div>
                  </div>
                )
              : null}
          </div>
        </Grid>
        <Grid item className={classes.gridItemForSelect}>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={
              props.sessionData[0].isSpecificationsOverridden
                ? false
                : !getNgsoType(NGSOTypeOptions.NGSOGold)
            }
            onClick={() => handleConcentartionPopup()}
          >
            <FormattedMessage {...messages.SELECT} />
          </Button>
        </Grid>
      </Grid>

      <div className={classes.description}>
        <sup>1</sup>Values determined via independent sources that used adapters
        manufactured with these processes. Multiplex sequencing was performed
        using Illumina Instruments.
      </div>
      <div className={clsx(classes.description, classes.paddingBottom)}>
        <sup>2</sup>Expected yields are valid for oligonucleotides 20 bases or
        longer.
      </div>

      {dialogOpen ? (
        <EditConcentration
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          selectedConcentration={selectedConcentration}
          UpdateNewConcentration={UpdateNewConcentration}
        />
      ) : (
        ''
      )}
    </div>
  )
}

export default ProductConfigurationsTable
